import { useEffect, useState, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { supabase } from '@/lib/supabase'
import {
  GraduationCap,
  Check,
  X,
  Star,
  ArrowLeft,
  Calendar,
  Loader2
} from 'lucide-react'

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || 'http://localhost:3000'
const PREMIUM_AMOUNT = 55000

interface DateOption {
  start_date: string
  schedule_day: string | null
  schedule_start_time: string | null
  schedule_end_time: string | null
  spots: number
}

export default function Plans() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [dateOptions, setDateOptions] = useState<DateOption[]>([])
  const [selectedIdx, setSelectedIdx] = useState<number | null>(null)
  const [isPaying, setIsPaying] = useState(false)
  const [payError, setPayError] = useState('')
  const formRef = useRef<HTMLFormElement>(null)

  useEffect(() => {
    loadAvailableDates()
  }, [])

  const loadAvailableDates = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/cohorts/available`)
      if (res.ok) {
        const data = await res.json()
        setDateOptions(data)
      }
    } catch (err) {
      console.error('Error loading available dates:', err)
    }
  }

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr + 'T12:00:00')
    return date.toLocaleDateString('es-ES', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    })
  }

  const selectedOption = selectedIdx !== null ? dateOptions[selectedIdx] : null

  const handlePremiumPayment = async (optionOverride?: DateOption | null) => {
    const option = optionOverride ?? selectedOption

    // Validar fecha seleccionada (solo si hay fechas disponibles)
    if (dateOptions.length > 0 && !option) {
      setPayError('Selecciona una fecha de inicio')
      return
    }

    setPayError('')
    setIsPaying(true)

    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.access_token) throw new Error('Debes iniciar sesión para realizar el pago')

      const res = await fetch(`${BACKEND_URL}/api/transbank/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          amount: PREMIUM_AMOUNT,
          user_id: user!.id,
          start_date: option?.start_date || null,
          schedule_day: option?.schedule_day || null,
          schedule_start_time: option?.schedule_start_time || null,
          schedule_end_time: option?.schedule_end_time || null
        })
      })

      const data = await res.json()

      if (!res.ok) {
        throw new Error(data.error || 'Error al crear el pago')
      }

      // Redirect a Transbank via form POST (requerido por Transbank)
      if (formRef.current) {
        const form = formRef.current
        form.action = data.url
        const tokenInput = form.querySelector('input[name="token_ws"]') as HTMLInputElement
        tokenInput.value = data.token
        form.submit()
      }
    } catch (err: any) {
      console.error('Error initiating payment:', err)
      setPayError(err.message || 'Error al iniciar el pago')
      setIsPaying(false)
    }
  }

  const handlePremiumClick = async () => {
    // Si no está logueado, ir a registro con flag premium (preservar fecha y horario)
    if (!user) {
      const params = new URLSearchParams({ plan: 'premium' })
      if (selectedOption) {
        params.set('start_date', selectedOption.start_date)
        if (selectedOption.schedule_day) params.set('schedule_day', selectedOption.schedule_day)
        if (selectedOption.schedule_start_time) params.set('schedule_start_time', selectedOption.schedule_start_time)
        if (selectedOption.schedule_end_time) params.set('schedule_end_time', selectedOption.schedule_end_time)
      }
      navigate(`/register?${params.toString()}`)
      return
    }

    await handlePremiumPayment()
  }

  const freeFeatures = [
    { included: true, text: 'Lección 1, Unidad 1' },
    { included: true, text: 'Ejercicios de la lección 1' },
    { included: false, text: 'Talleres en vivo' },
    { included: false, text: 'Exámenes' },
    { included: false, text: 'Certificado de finalización' }
  ]

  const premiumFeatures = [
    { text: '4 unidades completas' },
    { text: '16 lecciones' },
    { text: '64 contenidos' },
    { text: '384 ejercicios interactivos' },
    { text: '4 talleres en vivo' },
    { text: '4 exámenes' },
    { text: 'Certificado de finalización' }
  ]

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hidden form for Transbank POST redirect */}
      <form ref={formRef} method="POST" style={{ display: 'none' }}>
        <input type="hidden" name="token_ws" value="" />
      </form>

      {/* Header */}
      <header className="bg-white border-b">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <GraduationCap className="w-8 h-8 text-primary" />
            <span className="text-xl font-bold">
              Nomad<span className="text-[#EF4135]">Lexis</span>
            </span>
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Volver
          </Link>
        </div>
      </header>

      {/* Content */}
      <div className="container mx-auto px-4 py-12">
        {/* Title */}
        <div className="text-center mb-12">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
            Elige tu plan
          </h1>
          <p className="text-lg text-gray-600 max-w-xl mx-auto">
            Comienza gratis o accede al programa completo
          </p>
        </div>

        {/* Plans Grid */}
        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto items-start">

          {/* FREE Plan */}
          <div className="bg-white rounded-2xl border-2 border-gray-200 p-8 shadow-sm">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900">
                Gratis
              </h2>
              <p className="text-gray-500 mt-1">
                Prueba gratis por 7 días
              </p>
            </div>

            <div className="mb-8">
              <span className="text-4xl font-bold text-gray-900">$0</span>
              <span className="text-gray-500 ml-1">CLP</span>
            </div>

            <ul className="space-y-4 mb-8">
              {freeFeatures.map((feature, i) => (
                <li key={i} className="flex items-center gap-3">
                  {feature.included ? (
                    <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                      <Check className="w-3 h-3 text-green-600" />
                    </div>
                  ) : (
                    <div className="w-5 h-5 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                      <X className="w-3 h-3 text-gray-400" />
                    </div>
                  )}
                  <span className={feature.included ? 'text-gray-900' : 'text-gray-400'}>
                    {feature.text}
                  </span>
                </li>
              ))}
            </ul>

            <Link
              to="/register"
              className="block w-full py-3 text-center bg-primary text-white rounded-xl font-semibold hover:bg-primary/90 transition-colors"
            >
              Comenzar gratis
            </Link>
          </div>

          {/* PREMIUM Plan */}
          <div className="bg-white rounded-2xl border-2 border-amber-300 p-8 shadow-lg relative md:scale-105">
            {/* Badge */}
            <div className="absolute -top-4 left-1/2 -translate-x-1/2">
              <span className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-amber-500 text-white text-sm font-semibold rounded-full shadow-md">
                <Star className="w-4 h-4" />
                Recomendado
              </span>
            </div>

            <div className="mb-6 mt-2">
              <h2 className="text-2xl font-bold text-gray-900">
                Programa Completo
              </h2>
              <p className="text-gray-500 mt-1">
                Acceso total al programa
              </p>
            </div>

            <div className="mb-8">
              <span className="text-4xl font-bold text-gray-900">$55.000</span>
              <span className="text-gray-500 ml-1">CLP</span>
            </div>

            <ul className="space-y-4 mb-6">
              {premiumFeatures.map((feature, i) => (
                <li key={i} className="flex items-center gap-3">
                  <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                    <Check className="w-3 h-3 text-green-600" />
                  </div>
                  <span className="text-gray-900">{feature.text}</span>
                </li>
              ))}
            </ul>

            {/* Start Dates from Cohorts */}
            {dateOptions.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
                <div className="flex items-center gap-2 text-amber-800 mb-3">
                  <Calendar className="w-5 h-5" />
                  <p className="text-sm font-semibold">
                    Elige tu fecha de inicio
                  </p>
                </div>
                <div className="space-y-2">
                  {dateOptions.map((opt, idx) => (
                    <label
                      key={`${opt.start_date}-${opt.schedule_day}-${opt.schedule_start_time}`}
                      className={`flex items-center gap-3 p-2.5 rounded-lg cursor-pointer transition-colors ${
                        selectedIdx === idx
                          ? 'bg-amber-200/60 border border-amber-400'
                          : 'hover:bg-amber-100 border border-transparent'
                      }`}
                    >
                      <input
                        type="radio"
                        name="start_date"
                        value={idx}
                        checked={selectedIdx === idx}
                        onChange={() => setSelectedIdx(idx)}
                        className="w-4 h-4 text-amber-600 accent-amber-600"
                      />
                      <div>
                        <span className={`font-medium ${selectedIdx === idx ? 'text-amber-900' : 'text-amber-700'}`}>
                          {formatDate(opt.start_date)}
                        </span>
                        {opt.schedule_day && (
                          <span className={`block text-xs ${selectedIdx === idx ? 'text-amber-800' : 'text-amber-600'}`}>
                            {opt.schedule_day} de {opt.schedule_start_time} a {opt.schedule_end_time}
                          </span>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Error message */}
            {payError && (
              <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                {payError}
              </div>
            )}

            {/* Pay button */}
            <button
              onClick={handlePremiumClick}
              disabled={isPaying}
              className="block w-full py-3 text-center bg-amber-500 text-white rounded-xl font-semibold hover:bg-amber-600 transition-colors disabled:opacity-60 disabled:cursor-wait"
            >
              {isPaying ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Redirigiendo...
                </span>
              ) : (
                'Inscribirse al programa'
              )}
            </button>

            {!user && (
              <p className="text-xs text-gray-500 text-center mt-3">
                Deberás crear una cuenta antes de pagar
              </p>
            )}
          </div>
        </div>

        {/* Back link */}
        <div className="text-center mt-12">
          <Link
            to="/"
            className="text-sm text-gray-500 hover:text-primary transition-colors"
          >
            ← Volver al inicio
          </Link>
        </div>
      </div>
    </div>
  )
}
